import React, { useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Box, Container, Typography, Button, Link as MuiLink, Menu, MenuItem } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useOpenContactDialog } from '../contexts/ContactDialogContext';
import { analytics, AnalyticsEvent } from '../services/analytics';
import { MAIN_BG_COLOR } from '../theme';
import { PRODUCT_NAV_ITEMS } from '../pages/productPages';
import { CLIENT_TYPE_NAV_ITEMS } from '../pages/clientPages';

const NAV = {
    fontHeading: '"DM Serif Text", serif',
    fontBody: '"Inter", sans-serif',
    sectionPaddingX: '1.25rem',
} as const;

const solucionesMenuColumnTitleSx = {
    fontFamily: NAV.fontBody,
    fontWeight: 400,
    fontSize: '0.6875rem',
    color: 'rgba(255,255,255,0.45)',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.08em',
    px: 2,
    pt: 1.25,
    pb: 0.75,
    pointerEvents: 'none' as const,
};

const solucionesMenuItemSx = {
    fontFamily: NAV.fontBody,
    fontWeight: 400,
    fontSize: '0.9375rem',
    color: 'rgba(255,255,255,0.9)',
    py: 1.25,
    px: 2,
    gap: 1.25,
    whiteSpace: 'normal' as const,
    lineHeight: 1.35,
    borderRadius: 0,
    '&:hover': {
        background: 'rgba(255,255,255,0.08)',
        color: '#fff',
    },
};

const pillButtonWhite = {
    background: '#fff',
    color: MAIN_BG_COLOR,
    borderRadius: '100px',
    px: 2,
    py: 0.75,
    fontFamily: NAV.fontBody,
    fontWeight: 500,
    textTransform: 'none' as const,
    '&:hover': { background: 'rgba(255,255,255,0.9)' },
};

const navLinkSx = {
    fontFamily: NAV.fontBody,
    fontWeight: 400,
    fontSize: '1rem',
    color: 'rgba(255,255,255,0.9)',
    textDecoration: 'none',
    '&:hover': { color: '#fff' },
} as const;

const navLinkActiveSx = {
    fontWeight: 700,
    color: '#fff',
} as const;

function useIsNavActive(path: string): boolean {
    const { pathname } = useLocation();
    return pathname === path || pathname.startsWith(`${path}/`);
}

function NavRouteLink({
    to,
    children,
    compact = false,
}: {
    to: string;
    children: React.ReactNode;
    compact?: boolean;
}) {
    const active = useIsNavActive(to);

    return (
        <MuiLink
            component={Link}
            to={to}
            aria-current={active ? 'page' : undefined}
            sx={{
                ...navLinkSx,
                ...(active ? navLinkActiveSx : {}),
                fontSize: compact ? '0.9375rem' : navLinkSx.fontSize,
                flexShrink: 0,
            }}
        >
            {children}
        </MuiLink>
    );
}

function SolucionesNavMenu({ compact = false }: { compact?: boolean }) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);

    const clearCloseTimer = () => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    };

    const openMenu = () => {
        clearCloseTimer();
        if (buttonRef.current) setAnchorEl(buttonRef.current);
    };

    const scheduleCloseMenu = () => {
        clearCloseTimer();
        closeTimerRef.current = setTimeout(() => setAnchorEl(null), 120);
    };

    return (
        <Box
            onMouseEnter={openMenu}
            onMouseLeave={scheduleCloseMenu}
            sx={{ display: 'inline-flex' }}
        >
            <Button
                ref={buttonRef}
                id="soluciones-nav-button"
                aria-controls={open ? 'soluciones-nav-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                onClick={openMenu}
                endIcon={
                    <KeyboardArrowDownIcon
                        sx={{
                            fontSize: 18,
                            transition: 'transform 0.15s ease',
                            transform: open ? 'rotate(180deg)' : 'none',
                        }}
                    />
                }
                sx={{
                    ...navLinkSx,
                    fontSize: compact ? '0.9375rem' : navLinkSx.fontSize,
                    minWidth: 0,
                    px: 0,
                    py: 0,
                    textTransform: 'none',
                    background: 'transparent',
                    '&:hover': {
                        background: 'transparent',
                        color: '#fff',
                    },
                    '& .MuiButton-endIcon': {
                        ml: 0.25,
                    },
                }}
            >
                Soluciones
            </Button>
            <Menu
                id="soluciones-nav-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                disableScrollLock
                disablePortal
                MenuListProps={{
                    'aria-labelledby': 'soluciones-nav-button',
                    sx: { py: 0 },
                }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                PaperProps={{
                    sx: {
                        mt: 0.5,
                        minWidth: { xs: 240, sm: 480 },
                        maxWidth: 'calc(100vw - 2rem)',
                        borderRadius: '12px',
                        border: '0.5px solid rgba(255,255,255,0.12)',
                        background: MAIN_BG_COLOR,
                        boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
                        backgroundImage: 'none',
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                        py: 0.5,
                    }}
                >
                    <Box
                        sx={{
                            borderRight: {
                                xs: 'none',
                                sm: '0.5px solid rgba(255,255,255,0.1)',
                            },
                            borderBottom: {
                                xs: '0.5px solid rgba(255,255,255,0.1)',
                                sm: 'none',
                            },
                            pb: { xs: 0.5, sm: 0 },
                        }}
                    >
                        <Typography component="div" sx={solucionesMenuColumnTitleSx}>
                            Por caso de uso
                        </Typography>
                        {PRODUCT_NAV_ITEMS.map(({ label, to, Icon }) => (
                            <MenuItem
                                key={to}
                                component={Link}
                                to={to}
                                onClick={() => setAnchorEl(null)}
                                sx={solucionesMenuItemSx}
                            >
                                <Icon
                                    sx={{
                                        fontSize: 18,
                                        color: 'rgba(255,255,255,0.7)',
                                        flexShrink: 0,
                                    }}
                                />
                                {label}
                            </MenuItem>
                        ))}
                    </Box>
                    <Box sx={{ pt: { xs: 0.5, sm: 0 } }}>
                        <Typography component="div" sx={solucionesMenuColumnTitleSx}>
                            Por tipo de cliente
                        </Typography>
                        {CLIENT_TYPE_NAV_ITEMS.map(({ label, to, Icon }) => (
                            <MenuItem
                                key={label}
                                component={Link}
                                to={to}
                                onClick={() => setAnchorEl(null)}
                                sx={solucionesMenuItemSx}
                            >
                                <Icon
                                    sx={{
                                        fontSize: 18,
                                        color: 'rgba(255,255,255,0.7)',
                                        flexShrink: 0,
                                    }}
                                />
                                {label}
                            </MenuItem>
                        ))}
                    </Box>
                </Box>
            </Menu>
        </Box>
    );
}

function DesktopNavLinks() {
    return (
        <>
            <SolucionesNavMenu />
            <MuiLink
                href="https://radar.podscope.co"
                target="_blank"
                rel="noopener noreferrer"
                sx={navLinkSx}
            >
                Newsletter
            </MuiLink>
            <NavRouteLink to="/ranking">Ranking</NavRouteLink>
            <NavRouteLink to="/live">En Vivo</NavRouteLink>
        </>
    );
}

const MainNavbar: React.FC = () => {
    const openContactDialog = useOpenContactDialog();

    return (
        <Box
            component="header"
            sx={{
                position: 'sticky',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 1000,
                background: MAIN_BG_COLOR,
            }}
        >
            <Container
                maxWidth="lg"
                sx={{
                    px: { xs: NAV.sectionPaddingX, md: 4 },
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                }}
            >
                <Typography
                    component={Link}
                    to="/"
                    sx={{
                        fontFamily: NAV.fontHeading,
                        fontWeight: 800,
                        fontSize: { xs: '1.35rem', md: '1.5rem' },
                        color: '#fff',
                        textDecoration: 'none',
                        flexShrink: 0,
                    }}
                >
                    Podscope
                </Typography>

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: { xs: 1.25, md: 2.5 },
                        minWidth: 0,
                        ml: 'auto',
                    }}
                >
                    <Box
                        sx={{
                            display: 'none',
                            '@media (min-width: 720px)': {
                                display: 'flex',
                            },
                            alignItems: 'center',
                            gap: 2.5,
                        }}
                    >
                        <DesktopNavLinks />
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.25,
                            '@media (min-width: 720px)': { display: 'none' },
                        }}
                    >
                        <SolucionesNavMenu compact />
                        <NavRouteLink to="/ranking" compact>
                            Ranking
                        </NavRouteLink>
                        <NavRouteLink to="/live" compact>
                            En Vivo
                        </NavRouteLink>
                    </Box>

                    <Button
                        onClick={() => {
                            analytics.track(AnalyticsEvent.CLICK_REQUEST_DEMO, {
                                location: 'nav',
                            });
                            openContactDialog();
                        }}
                        sx={{
                            ...pillButtonWhite,
                            display: 'none',
                            '@media (min-width: 720px)': {
                                display: 'inline-flex',
                            },
                            px: 2,
                            py: 0.75,
                            fontSize: '0.875rem',
                            minWidth: 0,
                        }}
                    >
                        Agenda una demo
                    </Button>
                </Box>
            </Container>
        </Box>
    );
};

export default MainNavbar;
